import {
  Badge,
  DataTableColumn,
  FlexBox,
  Icon,
  IconButton,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  UnbanIpModal,
  UnbanIpModalOnCloseEvent,
} from 'components/shared/unbanIpModal/unbanIpModal';
import {
  BansByIpResponseDataBans,
  UsersGroupedByIpBan,
} from 'hooks/useBans/types';

import rowStyles from '../bansRow.module.scss';

const DELAY_FETCH = 1000;

// Table column components
// -----------------------
const renderIpAddress = ({
  ip_address,
}: BansByIpResponseDataBans): ReactNode => (
  <FlexBox alignItems="center">
    <Text.Paragraph className={rowStyles.userInfo}>{ip_address}</Text.Paragraph>
  </FlexBox>
);

const renderStatus = ({ bans }: BansByIpResponseDataBans): ReactNode =>
  bans[0]?.is_ip_address_banned ? (
    <Badge size="small" variant="error">
      Active
    </Badge>
  ) : (
    <Badge size="small" variant="secondary">
      Inactive
    </Badge>
  );

const renderNumberOfUsers = ({ bans }: BansByIpResponseDataBans): ReactNode =>
  bans.filter(
    (ban, index, self) =>
      self.findIndex((b) => b.end_user.id === ban.end_user.id) === index,
  ).length;

const renderLastBan = ({ bans }: BansByIpResponseDataBans): ReactNode => (
  <div className={rowStyles.lastBanInfo}>
    {bans[0]?.banned_at && (
      <div className={rowStyles.lastBanInfoDate}>
        <Text.Paragraph>
          <Timestamp relative time={bans[0].banned_at} />
        </Text.Paragraph>
      </div>
    )}

    {bans[0]?.banning_agent && (
      <div hidden className={rowStyles.lastBanInfoAgent}>
        by {bans[0].banning_agent.name}
      </div>
    )}
  </div>
);

const renderUnbanButtonWithRefreshItems = (refreshItems: () => void) =>
  function RenderUnbanButton({
    bans,
    ip_address,
  }: BansByIpResponseDataBans): ReactNode {
    const [isUnbanModalOpen, setIsUnbanModalOpen] = useState(false);

    return (
      bans[0]?.is_ip_address_banned && (
        <>
          <Tooltip content="Unban" dark>
            <IconButton
              variant="tertiary"
              icon="undo"
              onClick={(e) => {
                e.stopPropagation();
                setIsUnbanModalOpen(true);
              }}
            />
          </Tooltip>
          <UnbanIpModal
            ipAddress={ip_address}
            isOpen={isUnbanModalOpen}
            onClose={(event: UnbanIpModalOnCloseEvent) => {
              setIsUnbanModalOpen(false);
              if (!event.canceled && event.response) {
                setTimeout(() => {
                  refreshItems();
                }, DELAY_FETCH);
              }
            }}
          />
        </>
      )
    );
  };

// Sub-table column components
// ----------------------------
const renderChannelIcon = (ban: UsersGroupedByIpBan): ReactNode => (
  <Icon name="chat" style={{ verticalAlign: 'middle' }} />
);

const renderBannedUser = ({ end_user }: UsersGroupedByIpBan): ReactNode => (
  <Link to={`/contacts/user/${end_user.id}`}>{end_user.name}</Link>
);

const renderBannedAt = ({ banned_at }: UsersGroupedByIpBan): ReactNode => (
  <Timestamp relative time={banned_at} />
);

const renderConversation = ({ csid }: UsersGroupedByIpBan): ReactNode =>
  (csid && <Link to={`/conversation/${csid}`}>#{csid}</Link>) || (
    <Text.Paragraph as="span" subdued>
      n/a
    </Text.Paragraph>
  );

const renderBannedBy = ({ banning_agent }: UsersGroupedByIpBan): ReactNode => (
  <Link to={`/contacts/user/${banning_agent.id}`}>{banning_agent.name}</Link>
);

// Columns
// -------
export const tableColumns = (
  refreshItems: () => void,
): DataTableColumn<BansByIpResponseDataBans>[] => [
  {
    label: 'IP Address',
    selector: renderIpAddress,
  },
  {
    label: 'Status',
    style: { width: '30%' },
    selector: renderStatus,
  },
  { label: 'Users', style: { width: 160 }, selector: renderNumberOfUsers },
  { label: 'Last ban', style: { width: '25%' }, selector: renderLastBan },
  {
    style: { width: 1 },
    selector: renderUnbanButtonWithRefreshItems(refreshItems),
  },
];

export const subTableColumns: DataTableColumn<UsersGroupedByIpBan>[] = [
  { style: { width: 1 }, selector: renderChannelIcon },
  { label: 'Banned user', style: { width: '20%' }, selector: renderBannedUser },
  { label: 'Banned at', style: { width: '18%' }, selector: renderBannedAt },
  { label: 'Conversation', selector: renderConversation },
  { label: 'Banned by', selector: renderBannedBy },
];
